import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import classNames from 'classnames';
import toast from 'react-hot-toast';
import Tooltip from 'rc-tooltip';

import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faQuestionCircle, faTrash } from '@fortawesome/pro-light-svg-icons';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faBadgeCheck, faSync } from '@fortawesome/pro-solid-svg-icons';
import { faRedoAlt } from '@fortawesome/pro-regular-svg-icons';

import { Button as PrimeButton, ButtonProps } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ColumnDef, SortingState } from '@tanstack/react-table';

import { Button, FlexBox, Modal } from '@eltoro-ui/components';
import { ConfirmationModal, EmptyStatus, PageHeader, ReactTableSearchBar } from 'Components';
import Table from 'Components/Table';

import { usePermissionContext } from 'contexts/PermissionContext';

import ContactEligibleTag from 'Pages/MyContacts/components/ContactEligibleTag';
import ContactEnrichmentInformationModal from 'Pages/ProspectActivity/lenses/TalkToSphereLens/ContactEnrichmentInformationModal';
import { UploadCSVModal } from 'Pages/MyContacts/components';
import AddressesAccessor from 'Pages/ProspectActivity/lenses/TalkToSphereLens/TalkToSphereList/AddressesAccessor';
import CloudSpongeWidget from 'Pages/Dashboard/_Components/contactsGetter/CloudSpongeWidget';
import RedirectToMobileInfo from 'Pages/MyContacts/components/RedirectToMobileInfo/RedirectToMobileInfo';
import { VotingModal } from 'Components/VotingModal';
import { SaveAudienceWizard } from 'Pages/ProspectActivity/components';
import CustomIcons from 'assets/icons';

import contactsImporting from 'assets/Images/contacts-loading.gif';
import Uploadmodalcontacts from 'assets/Images/uploadmodalcontacts.png';
import Syncontacts from 'assets/Images/syncontacts.png';

import { deleteContacts, getContacts } from 'Requests';
import {
  enrichmentProcessStatus,
  retryEnrichmentProcess,
  uploadContactsSync,
} from 'Requests/Request_Methods/contactsMethods';

import { removeTimeFromDate } from 'Helpers';

import { GuideEnum } from 'enums';
import type { TContact, TResPagination, TRootState } from 'types';
import type {
  PanelRender,
  TablePaginationConfig,
  TableRowSelection,
  TableSortConfig,
} from 'Components/Table/types';

import './MyContacts.scss';

export const MyContactsPage = () => {
  const history = useHistory();

  const { userData } = useSelector((state: TRootState) => state.userReducer);
  const { userIsActive } = usePermissionContext();

  const uploadContactsRef = useRef<Menu>(null);
  const menuRootWrapperRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState('');
  const [contacts, setContacts] = useState<TContact[]>([]);
  const [eligibleContactsCount, setEligibleContactsCount] = useState<number>(0);
  const [uploadCSVModalIsOpen, setUploadCSVModalIsOpen] = useState(false);
  const [uploadcontactModal, setUploadcontactModal] = useState(false);
  const [synUploadModal, setSynUploadModal] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);
  const [syncIsLoading, setSyncIsLoading] = useState(false);

  const [contactEnrichmentInformationModalOpen, setContactEnrichmentInformationModalOpen] =
    useState(false);

  const [buttonActive, setButtonActive] = useState(true);
  const [loadFirst, setloadFirst] = useState(true);

  const [isSelectedAllData, setIsSelectedAllData] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<TContact[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<TResPagination>({ total_count: 0, total_pages: 0 });
  const [enrichState, setEnrichState] = useState({
    enrichment_percent: 0,
    has_failed_status: false,
    has_pending_status: false,
  });

  // ========== New Table Utilities
  const [fetchDataIsLoading, setFetchDataIsLoading] = useState(false);

  const handleSearchValue = (value: string) => {
    setSorting([]);
    setSearchValue(value);
    setPage(1);
  };
  const _fetchData = async (): Promise<void> => {
    setFetchDataIsLoading(true);
    try {
      const [sort] = sorting;
      let sort_key: string | null = null;
      let order: string | null = null;

      if (sort) {
        sort_key = sort.id;
        order = sort.desc ? 'desc' : 'asc';
      }

      const res = await getContacts(page, 50, searchValue, sort_key, order);
      await getEnrichmentProcessStatus();
      if (res.data?.contacts) {
        const {
          total_count,
          total_pages,
          eligible_contacts_count,
          contacts: resContacts,
        } = res.data;

        if (resContacts.length) {
          const guideBotContacts = sessionStorage.getItem('guideBotContacts');
          const contactsBotData = guideBotContacts ? JSON.parse(guideBotContacts) : null;
          if (contactsBotData?.type === GuideEnum.CONTACTS)
            sessionStorage.removeItem('guideBotContacts');
        }

        setContacts(resContacts);
        setPagination({ total_count, total_pages });
        setEligibleContactsCount(eligible_contacts_count);
      }
      setFetchDataIsLoading(false);
    } catch (err: any) {
      toast.error(err.detail);
      setFetchDataIsLoading(false);
    }
  };

  const getEnrichmentProcessStatus = async () => {
    try {
      const response = await enrichmentProcessStatus();
      if (response.data) {
        const { enrichment_percent, has_failed_status, has_pending_status } = response.data;
        setEnrichState({
          enrichment_percent,
          has_failed_status,
          has_pending_status,
        });
      }
    } catch (e) {
      if (e.detail && typeof e.detail === 'string') toast.error(e.detail);
      else toast.error('Something went wrong!');
    }
  };

  useEffect(() => {
    if (userIsActive) {
      _fetchData();
    }
  }, [page, searchValue, sorting?.[0]]);

  useEffect(() => {
    return () => {
      setContacts([]);
      setPagination({ total_count: 0, total_pages: 0 });
      setEnrichState({
        enrichment_percent: 0,
        has_failed_status: false,
        has_pending_status: false,
      });
    };
  }, []);

  const onViewDetail = (item: any) => {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <FontAwesomeIcon
          className="contact-link-icon"
          onClick={() =>
            history.push({
              pathname: `/my-contacts/${item.id}/contact-details`,
            })
          }
          icon={faChevronRight}
          size="1x"
          color="gray"
          style={{ cursor: 'pointer' }}
        />
      </div>
    );
  };

  const tableColumns: ColumnDef<TContact>[] = [
    {
      header: 'First Name',
      accessorKey: 'first_name',
      cell: ({ row: { original: contact } }) =>
        contact.first_name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {contact.enrichment_data && (
              <FontAwesomeIcon icon={faBadgeCheck} color="#FCD917" style={{ fontSize: 15 }} />
            )}
            <span>{contact.first_name}</span>
          </div>
        ) : (
          <>-</>
        ),
    },
    {
      header: 'Last Name',
      accessorKey: 'last_name',
      accessorFn: contact => contact.last_name || '-',
    },
    {
      header: 'Email Address',
      accessorKey: 'email',
      accessorFn: contact => contact.email || '-',
    },
    {
      header: 'Address',
      accessorKey: 'addresses',
      enableSorting: false,
      cell: ({ row: { original: contact } }) => <AddressesAccessor addresses={contact.addresses} />,
    },
    {
      header: () => (
        <FlexBox alignItems="center" gap="0.25rem">
          <span>Eligibility</span>
          <Tooltip
            placement="topLeft"
            classNames={{ root: classNames('audience-tooltip', 'contact-eligibility') }}
            showArrow
            align={{
              offset: [-20, -3],
              targetOffset: [-6, 0],
            }}
            overlay={
              <>
                Contact eligibility indicates whether a contact is complete and ready to be saved to
                a target audience. <br /> Add a full address to make your contacts eligible.
              </>
            }
          >
            <span style={{ display: 'flex', cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </Tooltip>
        </FlexBox>
      ),
      accessorKey: 'is_eligible',
      enableSorting: false,
      cell: ({ row: { original: contact } }) => (
        <ContactEligibleTag is_eligible={contact.is_eligible} />
      ),
    },
    {
      header: 'Date Updated',
      accessorKey: 'updated_at',
      accessorFn: contact => (contact.updated_at ? removeTimeFromDate(contact.updated_at) : '-'),
    },
    {
      id: 'link',
      size: 45,
      enableResizing: false,
      enableSorting: false,
      cell: ({ row: { original: contact } }) => onViewDetail(contact),
    },
  ];

  const onDelete = async (selectedContactsIds: Key[]) => {
    setDeleteIsLoading(true);
    try {
      const deleteBody = isSelectedAllData ? { is_all: true } : { ids: selectedContactsIds };
      await deleteContacts(deleteBody);

      const deletedDataLength = isSelectedAllData
        ? pagination.total_count
        : selectedContactsIds.length;

      toast.success(`${deletedDataLength} Contact${deletedDataLength > 1 ? 's' : ''} Deleted`);

      if (pagination.total_count - selectedContactsIds.length === 0) {
        sessionStorage.setItem(
          'guideBotContacts',
          JSON.stringify({
            isActive: true,
            type: GuideEnum.CONTACTS,
            msgType: '',
            count: 0,
          })
        );
      }

      setDeleteModalIsOpen(false);
      setDeleteIsLoading(false);
      setSelectedRowKeys([]);
      setSelectedContacts([]);
      setIsSelectedAllData(false);
      if (page === 1) _fetchData();
      else await setPage(1);
    } catch (err: any) {
      toast.error(err.detail);
      setDeleteIsLoading(false);
    }
  };

  const launchCloudSponge = () => {
    // @ts-ignore
    if (window.cloudsponge) {
      // @ts-ignore
      window.cloudsponge.launch();
    }
  };

  const emptyText = useMemo(() => {
    const heading = enrichState.has_pending_status
      ? 'We are importing your contacts!'
      : 'You still do not have any contacts here';

    const subHeading = enrichState.has_pending_status
      ? 'Please refresh after a while to see your customers’ list.'
      : 'Start by uploading or syncing your contacts list.';

    return searchValue && !contacts.length ? (
      <EmptyStatus
        heading="No results found"
        subHeading="You might consider trying different keywords, double-checking for any spelling errors, or adjusting your filters."
      />
    ) : (
      <EmptyStatus
        icon={enrichState.has_pending_status && <img src={contactsImporting} alt="loading..." />}
        heading={heading}
        subHeading={subHeading}
      >
        <FlexBox
          flexDirection="column"
          alignItems="center"
          UNSAFE_style={{ gap: '10px', marginBlockStart: '20px' }}
        >
          {/* for proccesing state */}
          {enrichState.has_pending_status ? (
            <Button
              size="l"
              kind="text"
              UNSAFE_className="refresh-contacts"
              onClick={() => {
                history.go(0);
              }}
            >
              Refresh
            </Button>
          ) : (
            <>
              <Button
                kind="primary"
                size="l"
                onClick={launchCloudSponge}
                disabled={buttonActive || syncIsLoading}
                iconLeft={
                  buttonActive || syncIsLoading ? (
                    <FontAwesomeIcon
                      icon={faSpinnerThird}
                      style={{ fontSize: 20, animation: 'spinner-border 0.75s linear infinite' }}
                    />
                  ) : (
                    <FontAwesomeIcon icon={faSync} style={{ fontSize: 20 }} />
                  )
                }
              >
                Sync Email Accounts
              </Button>
              <Button kind="text" size="l" onClick={() => setUploadCSVModalIsOpen(true)}>
                Upload Contacts
              </Button>
            </>
          )}
        </FlexBox>
      </EmptyStatus>
    );
  }, [enrichState, userData, syncIsLoading, buttonActive]);

  const afterSubmitContacts = async (cloudContacts: any) => {
    setSyncIsLoading(true);
    try {
      const contactsBody = cloudContacts.map((contact: any) => ({
        first_name: contact.first_name,
        last_name: contact.last_name,
        phone: contact.phone.map((phone: any) => ({
          number: phone.number,
          primary: phone?.primary,
          selected: phone?.selected,
        })),
        email: contact.email.map((email: any) => ({
          address: email.address,
          primary: email?.primary,
          selected: email?.selected,
        })),
        address: contact.address.map((address: any) => ({
          street: address.street,
          city: address.city,
          region: address.region,
          postal_code: address.postal_code,
        })),
      }));
      await uploadContactsSync(contactsBody);
      setSyncIsLoading(false);
      sessionStorage.setItem(
        'guideBotContacts',
        JSON.stringify({
          isActive: false,
        })
      );
      _fetchData();
    } catch (e) {
      setSyncIsLoading(false);
      toast.error('Oops, there was an issue with importing your contacts.');
    }
  };

  const uploadContactsMenuItems: MenuItem[] = [
    {
      label: 'Upload Contacts',
      command: () => setUploadCSVModalIsOpen(true),
    },
    {
      label: 'Sync Email Accounts',
      icon: syncIsLoading ? (
        <FontAwesomeIcon
          icon={faSpinnerThird}
          style={{
            fontSize: 20,
            animation: 'spinner-border 0.75s linear infinite',
          }}
        />
      ) : null,
      command: launchCloudSponge,
    },
  ];

  const toggleCreativeTypeSelect: ButtonProps['onClick'] = event => {
    uploadContactsRef?.current?.toggle(event);
  };

  const sortConfig: TableSortConfig = {
    sorting,
    onSortingChange: setSorting,
  };

  const rowSelection = useMemo<TableRowSelection<TContact>>(
    () => ({
      isSelectedAllData,
      onSelectAllData: selectedRows => {
        setSelectedRowKeys(selectedRows.length ? selectedRows.map(contacts => contacts.id) : []);
        setSelectedContacts(
          selectedRows.length
            ? prevContacts => {
                const filteredRows = prevContacts.filter(
                  row => contacts.findIndex(({ id }) => id === row.id) === -1
                );
                return [...filteredRows, ...selectedRows];
              }
            : []
        );
        setIsSelectedAllData(!!selectedRows.length);
      },
      selectedRowKeys,
      onChange: (selectedRowKeys, selectedRows) => {
        setIsSelectedAllData(false);
        setSelectedRowKeys(selectedRowKeys);
        setSelectedContacts(prevContacts => {
          const filteredRows = prevContacts.filter(
            row => contacts.findIndex(({ id }) => id === row.id) === -1
          );
          return [...filteredRows, ...selectedRows];
        });
      },
    }),
    [selectedRowKeys, contacts, isSelectedAllData]
  );

  const tablePagination = useMemo<TablePaginationConfig>(
    () => ({
      hideOnSinglePage: true,
      pageSize: 50,
      current: page,
      total: pagination.total_count,
      totalPages: pagination.total_pages,
      onChange: page => setPage(page),
    }),
    [page, pagination]
  );

  const tableTitle: PanelRender<TContact> | undefined = selectedRowKeys.length
    ? () => {
        return (
          <FlexBox alignItems="center" gap="0.75rem">
            <span className="selected-items-count">
              {`${isSelectedAllData ? pagination.total_count : selectedRowKeys.length} contact${
                selectedRowKeys.length > 1 ? 's' : ''
              } selected`}
            </span>
            <Divider data-pr-classname="table-header-divider" layout="vertical" />
            <PrimeButton
              text
              severity="danger"
              className="table-delete-btn"
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => setDeleteModalIsOpen(true)}
            />
          </FlexBox>
        );
      }
    : undefined;

  const handleContactEnrichmentInformationModal = (open: boolean) => () => {
    setContactEnrichmentInformationModalOpen(open);
  };

  const saveEligibleBtnDisable = useMemo(() => {
    const selectedEligibleContacts = selectedContacts.filter(contact => contact.is_eligible);

    if (selectedContacts.length) {
      return !selectedEligibleContacts.length;
    }

    return !eligibleContactsCount;
  }, [eligibleContactsCount, selectedContacts]);

  const onRetry = async () => {
    try {
      await retryEnrichmentProcess();
      await getEnrichmentProcessStatus();
    } catch (e) {
      toast('Something went wrong!');
    }
  };

  const enrichmentTag = useMemo(() => {
    if (enrichState.has_pending_status)
      return (
        <div className="enrich-tag">
          <FontAwesomeIcon
            icon={faSpinnerThird}
            color="#FFAB03"
            style={{
              fontSize: 20,
              animation: 'spinner-border 0.75s linear infinite',
            }}
          />
          <span className="enrich-text">Enriching Contacts...</span>
        </div>
      );

    if (enrichState.has_failed_status)
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div className="enrich-tag failed" onClick={onRetry}>
          <FontAwesomeIcon icon={faRedoAlt} color="#FF999E" />
          <span className="enrich-text">Retry Enrichment</span>
        </div>
      );

    return (
      <div className="enrich-tag">
        <FontAwesomeIcon icon={faBadgeCheck} color="#FCD917" style={{ fontSize: 20 }} />
        <span className="enrich-text">{enrichState.enrichment_percent}% enriched</span>
      </div>
    );
  }, [
    enrichState.enrichment_percent,
    enrichState.has_failed_status,
    enrichState.has_pending_status,
  ]);

  const forSaveAudienceData = useMemo(() => {
    if (selectedContacts.length && !isSelectedAllData) {
      const eligibleContactIds = selectedContacts
        .filter(contact => contact.is_eligible)
        .map(contact => contact.id);

      return {
        ids: eligibleContactIds,
      };
    }

    if (searchValue) {
      return {
        search: searchValue,
        eligibleCount: eligibleContactsCount,
      };
    }

    return {
      eligibleCount: eligibleContactsCount,
    };
  }, [eligibleContactsCount, selectedContacts, searchValue, isSelectedAllData]);

  return (
    <div className="MyContacts">
      {contactEnrichmentInformationModalOpen && (
        <ContactEnrichmentInformationModal
          potentialEnriched={
            <>
              Enrich <b>{enrichState.enrichment_percent}%</b> of your potential leads to run more
              effective campaigns.
            </>
          }
          onClose={handleContactEnrichmentInformationModal(false)}
        />
      )}
      {/* Header Section */}
      {enrichState.has_pending_status && userData?.sub_name !== 'premium' && (
        <PageHeader
          UNSAFE_CLASSNAME="MyContacts__PageHeader upgrade"
          subTitle={
            <FlexBox alignItems="center" UNSAFE_style={{ gap: '15px' }}>
              <FontAwesomeIcon
                icon={faSpinnerThird}
                color="#FFAB03"
                style={{
                  fontSize: 32,
                  animation: 'spinner-border 0.75s linear infinite',
                }}
              />
              <span className="enrich-text-checking">
                Checking your contacts’ data to show enrichment potential…
              </span>
            </FlexBox>
          }
        />
      )}
      <FlexBox flexDirection="column">
        {!!contacts?.length &&
          userData?.sub_name !== 'premium' &&
          !enrichState.has_failed_status &&
          !enrichState.has_pending_status &&
          !!enrichState.enrichment_percent && (
            <PageHeader
              UNSAFE_CLASSNAME="MyContacts__PageHeader upgrade"
              subTitle={
                <FlexBox alignItems="center" UNSAFE_style={{ gap: '15px' }}>
                  <FontAwesomeIcon icon={faBadgeCheck} color="#FCD917" className="enriched-badge" />
                  <span className="enrich-text-potential">
                    Enrich <b>{enrichState.enrichment_percent}%</b> of your potential leads to run
                    more effective campaigns.
                  </span>
                </FlexBox>
              }
              actions={[
                <button
                  key="Learn more"
                  className="learn-more-btn"
                  onClick={handleContactEnrichmentInformationModal(true)}
                >
                  Learn more
                </button>,
              ]}
            />
          )}
        <PageHeader
          UNSAFE_CLASSNAME="MyContacts__PageHeader"
          subTitle={
            <FlexBox alignItems="center" UNSAFE_style={{ gap: '15px' }}>
              <span>
                You have <b>{pagination.total_count} contacts</b>
              </span>
              {!!contacts?.length && userData?.sub_name === 'premium' && (
                <FlexBox gap="0.25rem" alignItems="center">
                  {enrichmentTag}
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    onClick={handleContactEnrichmentInformationModal(true)}
                    style={{ color: '#000000', fontSize: 20, cursor: 'pointer' }}
                  />
                </FlexBox>
              )}
            </FlexBox>
          }
          actions={[
            <ReactTableSearchBar
              key="campaign-searchbar"
              handleSearchValue={handleSearchValue}
              placeholder="Search by first name, last name"
              searchValue={searchValue}
            />,
            ...((searchValue && !contacts.length) || contacts?.length
              ? [
                  <div
                    key="upload-contacts"
                    className="upload-contacts-toggle-wrapper"
                    ref={menuRootWrapperRef}
                  >
                    <Menu
                      id="popup_menu_right"
                      className="upload-contacts-menu"
                      popupAlignment="right"
                      popup
                      appendTo={() => menuRootWrapperRef.current as HTMLDivElement}
                      model={uploadContactsMenuItems}
                      ref={uploadContactsRef}
                    />
                    <PrimeButton
                      className="upload-contacts-toggle Button Button--l-size Button--has-left-icon upgrade-button"
                      label="Upload/Sync"
                      icon={<CustomIcons name="upload" fontSize={20} />}
                      onClick={toggleCreativeTypeSelect}
                      aria-controls="popup_menu_left"
                      aria-haspopup
                    />
                  </div>,
                ]
              : []),
            <Tooltip
              key="save-eligible-contacts"
              classNames={{ root: 'audience-tooltip save-eligible-contacts' }}
              showArrow
              align={{
                offset: [-15, -3],
                targetOffset: [-6, 0],
              }}
              placement="top"
              overlay="To save, please ensure at least one contact is eligible."
              {...(saveEligibleBtnDisable ? {} : { visible: false })}
            >
              <span>
                <SaveAudienceWizard
                  type="contacts"
                  saveText="Save Eligible Contacts"
                  dataObj={forSaveAudienceData}
                  disabled={saveEligibleBtnDisable}
                  onSaveAndClose={() => {}}
                />
              </span>
            </Tooltip>,
          ]}
        />
        {uploadcontactModal && (
          <Modal offClick={() => setUploadcontactModal(false)}>
            <VotingModal
              handleModel={() => setUploadcontactModal(false)}
              icons={Uploadmodalcontacts}
              title="Upload Contacts"
              subTitle="Upload or enter your contacts to receive notifications when your contacts are home shopping and activate advertising."
            />
          </Modal>
        )}
        {synUploadModal && (
          <Modal offClick={() => setSynUploadModal(false)}>
            <VotingModal
              handleModel={() => setSynUploadModal(false)}
              icons={Syncontacts}
              title="Sync Contacts"
              subTitle="Sync email contacts to receive notifications when your contacts are home shopping and activate advertising."
            />
          </Modal>
        )}
      </FlexBox>
      <Table<TContact>
        label="contacts"
        columns={tableColumns}
        data={contacts}
        loading={fetchDataIsLoading}
        rowSelection={rowSelection}
        sortConfig={sortConfig}
        pagination={tablePagination}
        title={tableTitle}
        emptyText={emptyText}
      />
      {deleteModalIsOpen && (
        <ConfirmationModal
          heading={`Are you sure you want to delete ${
            isSelectedAllData ? pagination.total_count : selectedRowKeys.length
          } contact${
            (isSelectedAllData ? pagination.total_count : selectedRowKeys.length) > 1 ? 's' : ''
          }?`}
          note="Contacts currently being enriched won’t be deleted."
          onSubmit={() => onDelete(selectedRowKeys)}
          onCancel={() => setDeleteModalIsOpen(false)}
          loading={deleteIsLoading}
          okText="Yes, Delete"
          cancelText="No, Thanks"
          deletion
        />
      )}
      {!contacts.length && <RedirectToMobileInfo />}
      {uploadCSVModalIsOpen && (
        <UploadCSVModal
          onClose={(e?: boolean) => {
            if (e) {
              _fetchData();
            }
            setUploadCSVModalIsOpen(false);
          }}
        />
      )}
      <CloudSpongeWidget
        afterSubmitContacts={afterSubmitContacts}
        setButtonActive={setButtonActive}
        loadFirst={loadFirst}
        buttonActive={buttonActive}
        onCloseTypea={() => {
          setloadFirst(false);
        }}
      />
    </div>
  );
};
